import React, { useEffect, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useStatusStyle } from '@europrocurement/l2d-modules/hooks';
import { ReactGridContainer } from '@europrocurement/flexy-components';
import { type ResizeHandleAxis } from 'react-grid-layout';
import { faBuildingColumns } from '@fortawesome/sharp-regular-svg-icons';
import { useSnackbar } from 'notistack';
import { DOSSIERS_OPEN_SEARCH_SLICE_NAME, useFormaliteService } from '@europrocurement/l2d-domain';
import {
    VALIDATOR_TYPE_GREFFE,
    VALIDATOR_TYPE_GREFFE_INPI,
} from '@europrocurement/l2d-modules/modules/Dossiers';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import DashboardStatCardTotalBar from '../molecules/DashboardStatCardTotalBar/DashboardStatCardTotalBar';
import WidgetWithAction from '../molecules/WidgetWithAction/WidgetWithAction';
import { type DashboardFiltersType } from '../../containers/FormalistDashboard/FormalistDashboard';

type GreffesStatusWidgetDashboardProps = {
    localStorageName: string;
    formalistIds: number[];
    filters: DashboardFiltersType;
    listeDossiersPath: string;
    formalistName?: string;
};

type MergedGreffeStatus = {
    libelleStatus: string;
    statusFlag: number;
    childsCount: number;
    idStatuses: number[];
    sortOrder?: number | null;
};

const RESIZE_HANDLES = ['s', 'w', 'e', 'n', 'sw', 'nw', 'se', 'ne'] as ResizeHandleAxis[];

const GreffesStatusWidgetDashboard: React.FunctionComponent<GreffesStatusWidgetDashboardProps> =
    function ({ formalistIds, filters, listeDossiersPath, localStorageName, formalistName }) {
        const { palette } = useTheme();
        const { getValidatorStatusStyle } = useStatusStyle();
        const { enqueueSnackbar } = useSnackbar();
        const { dossierOpenSearchApi } = useFormaliteService();
        const dispatch = useDispatch();
        const navigate = useNavigate();

        const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
        const [hasFetched, setHasFetched] = useState<boolean>(false);
        const [mergedStatuses, setMergedStatuses] = useState<MergedGreffeStatus[]>([]);

        const handleGreffeStatusClick = (statusIds: number[]) => {
            if (statusIds.length === 0) return;

            dispatch({ type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/resetmainFilter` });
            dispatch({
                type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
                payload: { key: 'formalisteId', value: formalistIds },
            });
            dispatch({
                type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
                payload: { key: 'dateCreation', value: filters },
            });
            dispatch({
                type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
                payload: { key: 'statutValideurId', value: statusIds },
            });

            if (formalistName) {
                dispatch({
                    type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilterLabel`,
                    payload: { key: 'formalisteId', label: formalistName },
                });
            }

            navigate(`/${listeDossiersPath}`);
        };

        // Recap uses statusFlag as id — resolve back to all idStatuses for that flag
        const handleRecapItemClick = (statusFlag?: number | null) => {
            if (!statusFlag) return;
            const match = mergedStatuses.find((s) => s.statusFlag === statusFlag);
            if (match) handleGreffeStatusClick(match.idStatuses);
        };

        const fetchGreffesStatus = async () => {
            if (isSubmitting && !formalistIds) return;

            setIsSubmitting(true);
            const dossierStatusResponse =
                await dossierOpenSearchApi.apiOpensearchValideursStatusGetCollection({
                    formalisteId: formalistIds,
                    itemsPerPage: 20,
                    startDate: filters.startDate,
                    endDate: filters.endDate,
                });

            setIsSubmitting(false);
            setHasFetched(true);

            if (dossierStatusResponse.status === 200) {
                const allStatuses = dossierStatusResponse.data['hydra:member']
                    .filter(
                        (status) =>
                            [VALIDATOR_TYPE_GREFFE_INPI, VALIDATOR_TYPE_GREFFE].includes(
                                status.idValideur as number,
                            ) &&
                            status.sortOrder != null &&
                            status.libelleStatus !== 'Validé',
                    )
                    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

                // Merge by libelleStatus, summing childsCount
                const map = new Map<string, MergedGreffeStatus>();
                allStatuses.forEach((status) => {
                    const key = status.libelleStatus ?? '';
                    const existing = map.get(key);
                    if (existing) {
                        existing.childsCount += (status.childsCount as number) || 0;
                        existing.idStatuses.push(status.idStatus as number);
                    } else {
                        map.set(key, {
                            libelleStatus: key,
                            statusFlag: status.statusFlag as number,
                            childsCount: (status.childsCount as number) || 0,
                            idStatuses: [status.idStatus as number],
                            sortOrder: status.sortOrder,
                        });
                    }
                });

                setMergedStatuses(
                    Array.from(map.values()).sort(
                        (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
                    ),
                );
            } else {
                enqueueSnackbar('Une erreur est survenue lors de la récupération des dossiers', {
                    variant: 'error',
                });
                setMergedStatuses([]);
            }
        };

        useEffect(() => {
            fetchGreffesStatus();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [formalistIds, filters.startDate, filters.endDate]);

        if (!hasFetched) return null;

        const layoutProps = [
            {
                i: `${localStorageName}_recap`,
                x: 0,
                y: 0,
                w: 2,
                h: 1,
                isResizable: true,
                resizeHandles: RESIZE_HANDLES,
            },
            ...mergedStatuses.map((status, i) => ({
                i: `${localStorageName}_${status.libelleStatus}`,
                x: i % 2,
                y: Math.floor(i / 2) + 1,
                w: 1,
                h: 1,
                isResizable: true,
                resizeHandles: RESIZE_HANDLES,
            })),
        ];

        const recapBoxSx = {
            width: '100%',
            height: '100%',
            backgroundColor: palette.background.paper,
            borderRadius: 2,
            boxShadow: `0px 2px 2.5px 0px ${palette.background.shadow}`,
            display: 'flex',
            flexDirection: 'column',
            p: 2,
        };

        return (
            <ReactGridContainer
                localStorageName={localStorageName}
                gridconfig={{ cols: 2, rowHeight: 200 }}
                layoutProps={layoutProps}
            >
                <Box
                    key={`${localStorageName}_recap`}
                    sx={recapBoxSx}
                >
                    <DashboardStatCardTotalBar
                        items={mergedStatuses.map((status) => ({
                            id: status.statusFlag,
                            libelle: status.libelleStatus,
                            childsCount: status.childsCount,
                            key: status.libelleStatus,
                        }))}
                        title="Suivi Greffe — flux actif"
                        icon={faBuildingColumns}
                        handleItemClick={handleRecapItemClick}
                        handleStyleStatus={getValidatorStatusStyle}
                    />
                </Box>
                {mergedStatuses.map((status) => {
                    const { paletteString, namedIcon } = getValidatorStatusStyle(status.statusFlag);
                    return (
                        <Box key={`${localStorageName}_${status.libelleStatus}`}>
                            <WidgetWithAction
                                title={
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography
                                            variant="l2dh6"
                                            fontWeight="bold"
                                        >
                                            {status.libelleStatus}
                                        </Typography>
                                    </Box>
                                }
                                icon={namedIcon}
                                actionMessage="Voir"
                                actionCallback={async () =>
                                    handleGreffeStatusClick(status.idStatuses)
                                }
                                data={status.childsCount || '0'}
                                color={paletteString}
                            />
                        </Box>
                    );
                })}
            </ReactGridContainer>
        );
    };

export default GreffesStatusWidgetDashboard;
